import { FC, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { AuthContext } from "context/appContext";
import { getLocationByUserId, resetValues } from "redux/features/location/locationSlice";
import { Location } from "data/types";
import { itemPerDashboradClient } from "data/constants";
import ModernApprovedSpacesHeader from "./ModernApprovedSpacesHeader";
import ModernApprovedSpacesGrid from "./ModernApprovedSpacesGrid";

export interface ModernApprovedSpacesContainerProps {
  className?: string;
}

const ModernApprovedSpacesContainer: FC<ModernApprovedSpacesContainerProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;
  const [loading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useAppDispatch();
  const locationStates = useAppSelector((state) => state.location?.locationData || []) as Location[];

  // Filter for ALL locations (approved, drafts, pending, etc.)
  const allLocations = Array.isArray(locationStates) ? locationStates : [];

  // Pagination
  const itemsPerPage = itemPerDashboradClient;
  const paginatedLocations = allLocations?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(allLocations?.length / itemsPerPage);
  const totalCount = allLocations?.length || 0;

  // Statistics for header
  const approvedCount = allLocations.filter((loc) => loc.validation === "approuvé").length;
  const pendingCount = allLocations.filter((loc) => loc.validation === "en attente").length;
  const draftCount = allLocations.filter((loc) => loc.validation === "brouillon").length;
  const blockedCount = allLocations.filter((loc) => loc.validation === "bloqué").length;
  const processingCount = allLocations.filter((loc) => loc.validation === "en cours").length;

  // Load locations
  useEffect(() => {
    if (profilId) {
      setIsLoading(true);
      dispatch(getLocationByUserId(String(profilId))).finally(() => setIsLoading(false));
    }
  }, [profilId, dispatch]);

  // Reset values on mount
  useEffect(() => {
    dispatch(resetValues());
  }, [dispatch]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleAddSpace = () => {
    navigate("/add-location");
  };

  const handleFilter = (status?: string) => {
    // TODO: Implement filter functionality
    console.log("Filter clicked for status:", status);
  };

  const handleDeleteSpace = (locationId: string) => {
    // TODO: Implement delete functionality
    console.log("Delete space:", locationId);
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50/50 via-green-50/20 to-emerald-50/30 dark:from-gray-900 dark:via-green-950/20 dark:to-emerald-950/30 ${className}`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="container mx-auto px-4 py-8"
      >
        <ModernApprovedSpacesHeader
          totalCount={totalCount}
          approvedCount={approvedCount}
          pendingCount={pendingCount}
          draftCount={draftCount}
          blockedCount={blockedCount}
          processingCount={processingCount}
          onAddSpace={handleAddSpace}
          onFilter={handleFilter}
        />

        <ModernApprovedSpacesGrid
          locations={paginatedLocations}
          isLoading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onDeleteSpace={handleDeleteSpace}
        />
      </motion.div>
    </div>
  );
};

export default ModernApprovedSpacesContainer;
